import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';
import { buildTioAuthHeaders } from '../utils/auth-headers.ts';
import downloadFile from '../utils/download-file.ts';
import type { PaymentManner } from '../data/resources/tas-payment.type.ts';

const tasPaymentsUrl = (path?: string) => {
  const opts: QueryUrlOptions = {
    identifier: { type: 'tas-payments' },
    op: 'query',
    resourcePath: 'tas-payments',
  };
  const base = buildBaseURL(opts);
  return path ? `${base}/${path.replace(/^\/+/, '')}` : base;
};

/** POST /tas-payments/create_payment — admin only. */
export function createTasPaymentRequest(
  companyId: string,
  paymentManner?: PaymentManner
): RequestInfo {
  return {
    url: tasPaymentsUrl('create_payment'),
    method: 'POST',
    body: JSON.stringify({
      company_id: companyId,
      ...(paymentManner && { payment_manner: paymentManner }),
    }),
    cacheOptions: { [SkipCache]: true },
  };
}

/** POST /tas-payments/make_payment — admin only. Create + settle. */
export function createAndSettleTasPaymentRequest(
  companyId: string,
  paymentManner?: PaymentManner
): RequestInfo {
  return {
    url: tasPaymentsUrl('make_payment'),
    method: 'POST',
    body: JSON.stringify({
      company_id: companyId,
      ...(paymentManner && { payment_manner: paymentManner }),
    }),
    cacheOptions: { [SkipCache]: true },
  };
}

/** PUT /tas-payments/:id/settle_payment — admin + employee admin views. */
export function settleTasPaymentRequest(paymentId: string): RequestInfo {
  return {
    url: tasPaymentsUrl(`${paymentId}/settle_payment`),
    method: 'PUT',
    cacheOptions: { [SkipCache]: true },
  };
}

/** DELETE /tas-payments/:id/cancel_payment — admin + employee admin views. */
export function cancelTasPaymentRequest(paymentId: string): RequestInfo {
  return {
    url: tasPaymentsUrl(`${paymentId}/cancel_payment`),
    method: 'DELETE',
    cacheOptions: { [SkipCache]: true },
  };
}

// Blob downloads bypass JSON:API negotiation and use explicit auth headers with direct fetch.

/** Build the payment-files download URL: `{host}/tas-payments/{id}/download_files`.
 * Mirrors the admin payments page's
 * `[adapter.buildURL('tas-payments'), paymentId, 'download_files'].join('/')`. */
export function tasPaymentDownloadFilesUrl(paymentId: string): string {
  return tasPaymentsUrl(`${paymentId}/download_files`);
}

/**
 * Streams payment files and triggers a tas_payment_files.zip download with MIME type
 * application/x-zip on a successful response. Uses auth headers without Content-Type or Accept.
 * @param apiKey The per-app ENV.apiKey.
 */
export async function downloadTasPaymentFiles(
  paymentId: string,
  { apiKey }: { apiKey: string }
): Promise<void> {
  const headers = buildTioAuthHeaders({ apiKey });

  const response = await fetch(tasPaymentDownloadFilesUrl(paymentId), { headers });
  if (response.ok) {
    downloadFile(await response.blob(), 'tas_payment_files.zip', 'application/x-zip');
  }
}
